import { useSpeakerLabels } from '@src/hooks/useSpeakerLabels';
import { adjustDOMClassesToTopicHighlight } from '@src/utility/topicHighlightDom';
import {
  groupElementsByParagraph,
  type TranscriptParagraphGroup,
} from '@src/utility/transcriptGrouping';
import {
  clipHighlightStyles,
  overlayHighlightStyles,
  quoteHighlightStyles,
  savedSegmentHighlightStyles,
} from '@src/utility/transcriptQuoteUtils';
import { searchHighlightStyles } from '@src/utility/transcriptSearchUtils';
import chunk from 'lodash/chunk';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

import {
  ChapterHeadingRow,
  SectionHeadingRow,
  StructureGapRow,
} from './reelStructure/ReelStructureHeadings';
import {
  buildReelTranscriptRows,
  type ReelSplitGroup,
  splitGroupsAtReelSections,
} from './reelStructure/reelTranscriptRows';
import { useReelTranscriptStructure } from './reelStructure/ReelTranscriptStructureContext';
import { revealCollapsedWord } from './reelStructure/revealCollapsedWord';
import {
  SpeakerHeader,
  SpeakerLabel,
  SpeakerTime,
} from './speakerHeaderStyles';
import { CardChip, NametagChip } from './TimelineMarkerChip';
import { CURSOR_ALLOWANCE_PX, topicRailCss } from './topicRailStyles';

import type { TranscriptElement } from '../../types.ts/transcript';
import type { ClipTimelineMarkers } from './hooks/useTimelineMarkers';
import type { MarkerClickHandler } from './TimelineMarkerChip';
import type { ReelWordStamp } from '@src/services/reel/reelTranscriptStructure';
import type { PunchListItem } from '@src/types.ts/story';

/**
 * Formats time as MM:SS
 * @param seconds Time in seconds
 * @returns Formatted time string
 */
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins.toString().padStart(2, '0')}:${secs
    .toString()
    .padStart(2, '0')}`;
};

type ElementProps = {
  value: string | null;
  state: TranscriptElement['state'];
  index: number;
  /**
   * Source `initial_index` — stamped as `data-initial-index` so the original
   * pane shares the translated pane's index space. The Translation Review
   * linked cross-language highlight (PLAT-10287) keys both panes on
   * `initial_index`; harmless extra attribute everywhere else.
   */
  initialIndex: number;
  /**
   * PLAT-10611 (F5) — the source element's STABLE id (`source_uuid ?? id`),
   * stamped as `data-source-uuid` so the cross-language link + the span-replace
   * original-pane read resolve this word by identity FIRST (drift-correct),
   * falling back to `data-initial-index`. Omitted (attribute absent) when the
   * element has no id (whitespace / un-migrated).
   */
  sourceUuid?: string;
  // isHighlighted: boolean;
  isPhotoHighlight: boolean;
  isLastPhotoHighlight: boolean;
  isPhotoAssigned: boolean;
};

const Element = React.memo(
  (props: ElementProps) => {
    const { value, index, initialIndex, sourceUuid } = props;
    return (
      <WordLight
        key={`element-${index}`}
        data-index={index}
        data-initial-index={initialIndex}
        data-source-uuid={sourceUuid}
        className=""
      >
        {value}
      </WordLight>
    );
  },
  // Update only if content or either source-key stamp changes.
  (prevProps, nextProps) =>
    prevProps.value === nextProps.value &&
    prevProps.initialIndex === nextProps.initialIndex &&
    prevProps.sourceUuid === nextProps.sourceUuid,
);

export type SpeakerClickInfo = {
  speakerLabel: string;
  /** Element indices covered by this paragraph section */
  sectionIndices: number[];
  /** The DOM element clicked, for positioning context menus */
  targetElement: HTMLElement;
};

type PhotoHighlightViewData = {
  punchListItem: PunchListItem | undefined;
  lastPhotoHighlightActive: boolean;
  lastPhotoHighlightElementId: string | undefined;
};

type Props = {
  cursor: { from: number; to: number };
  transcriptElements: TranscriptElement[] | undefined;
  scrollRef: React.RefObject<HTMLDivElement>;
  onSpeakerClick?: (info: SpeakerClickInfo) => void;
  /**
   * Optional inline visual markers for clip-scoped timeline elements
   * (intro card, end card, nametags). Provided by ClipTranscriptBridge.
   */
  timelineMarkers?: ClipTimelineMarkers;
  /**
   * PLAT-10045 — invoked when the user clicks a `CardChip` / `NametagChip`.
   * Receives the marker + the chip's DOM element so the caller can anchor
   * a follow-up popover. Optional — chips stay non-interactive when omitted.
   */
  onMarkerClick?: MarkerClickHandler;
  /** When true, suppress the green photo/punch-list highlights on transcript words */
  hidePhotoHighlights?: boolean;
  /** Current playback state — drives cursor highlight logic. */
  isPlaying: boolean;
  /** Re-derives word classes when the punch list changes. */
  punchListSignature: string;
  /** Returns highlight view data for a transcript word at the given index. */
  getPhotoHighlightViewData: (index: number) => PhotoHighlightViewData;
  /**
   * When set, only paragraphs whose paragraph_id is in this set render.
   * Undefined = render all paragraphs (default).
   */
  visibleParagraphIds?: ReadonlySet<string>;
  /**
   * When set, only elements whose index is in this set render (within the
   * already-visible paragraphs) — used by the highlights "Show Only" filter to
   * show just the highlighted words instead of the whole paragraph. Undefined =
   * render every element (default).
   */
  visibleElementIndices?: ReadonlySet<number>;
  /**
   * When set, paragraphs whose paragraph_id is in this set get the topic
   * rail in the left gutter (does not filter the render). Undefined = no
   * highlighting.
   */
  highlightedParagraphIds?: ReadonlySet<string>;
  /**
   * Per-paragraph rail colors keyed by paragraph_id. Falls back to the
   * default blue when missing or undefined.
   */
  paragraphHighlightColors?: ReadonlyMap<string, string>;
  /**
   * Paragraph_id of the currently-active topic match — gets a solid rail
   * while the other matches stay muted. Optional.
   */
  activeParagraphId?: string;
};

export const TranscriptionText: React.FC<Props> = observer(
  ({
    cursor,
    transcriptElements,
    scrollRef,
    onSpeakerClick,
    timelineMarkers,
    onMarkerClick,
    hidePhotoHighlights = false,
    isPlaying,
    punchListSignature,
    getPhotoHighlightViewData,
    visibleParagraphIds,
    visibleElementIndices,
    highlightedParagraphIds,
    paragraphHighlightColors,
    activeParagraphId,
  }) => {
    // UPDATE REASON 1: cursor moves or playback state changes
    const prevCursor = useRef<{ from: number; to: number } | null>(null);
    const prevIsPlaying = useRef(false);
    useEffect(() => {
      const isPlayingChanged = isPlaying !== prevIsPlaying.current;
      prevIsPlaying.current = isPlaying;

      if (
        !isPlayingChanged &&
        cursor.from === prevCursor.current?.from &&
        cursor.to === prevCursor.current?.to
      ) {
        return;
      }

      adjustDOMClassesToCursor(
        cursor,
        isPlayingChanged && cursor.from === prevCursor.current?.from
          ? cursor
          : prevCursor.current,
        scrollRef,
        isPlaying,
      );

      prevCursor.current = cursor;
    }, [cursor, isPlaying]);

    // UPDATE REASON 2: transcript elements or punchlist data change
    const prevClasses = useRef<string[]>([]);
    useEffect(() => {
      (async () => {
        if (!transcriptElements?.length) {
          return;
        }

        const newClasses = transcriptElements.map((el, index) =>
          transcriptElementToWordClassName(
            el,
            index,
            getPhotoHighlightViewData,
            hidePhotoHighlights,
          ),
        );

        await adjustDOMClassesToElementStateUpdates(
          newClasses,
          prevClasses.current,
          scrollRef,
        );

        prevClasses.current = newClasses;
      })();
    }, [transcriptElements, punchListSignature, hidePhotoHighlights]);

    // PLAT-11157/11159 — the reel structure context and the per-word stamp,
    // computed ONCE here against this component's own element array (the
    // rendered projection's indices drift one token per section seam from
    // getFullTranscript()'s) and shared with the inner renderer as a prop.
    const reelView = useReelTranscriptStructure();
    // Keyed on `stampWords` rather than the whole view: the view's identity
    // also changes when a Chapter is folded, and re-stamping every token
    // because the creator collapsed a row is work for an answer that cannot
    // have changed. The host keeps `stampWords` stable across folds.
    const stampWords = reelView?.stampWords;
    const reelWordStamp = useMemo(
      () =>
        stampWords && transcriptElements
          ? stampWords(transcriptElements)
          : undefined,
      [stampWords, transcriptElements],
    );

    // PLAT-11160 — the Section under the playhead, read off the cursor the
    // transcript already maintains: the cursor's word resolves to its Section
    // through the same stamp the headings were placed with, so the marked
    // heading and the words being spoken cannot disagree. The Reel Editor
    // marks by the same boundary reading (a boundary instant belongs to the
    // Section that starts there).
    const activeSectionId = reelWordStamp?.sectionIdByWordIndex.get(
      cursor.from,
    );

    // PLAT-11159 — playback and a restored position reach folded words by
    // opening them: when the cursor MOVES into a folded passage (playback
    // advancing, a seek, the mount position), the passage opens. A cursor
    // merely sitting still while the creator folds around it opens nothing —
    // only a transition reveals, so a fold is never fought by a re-render.
    const prevCursorFromRef = useRef<number | null>(null);
    useEffect(() => {
      const from = cursor.from;
      const isMount = prevCursorFromRef.current === null;
      if (prevCursorFromRef.current === from) return;
      prevCursorFromRef.current = from;
      if (!reelWordStamp || !transcriptElements) return;
      revealCollapsedWord(
        reelView,
        transcriptElements,
        from,
        reelWordStamp,
        isMount ? 'mount' : 'playback',
      );
    }, [cursor.from, reelView, reelWordStamp, transcriptElements]);

    // UPDATE REASON 3: topic highlight / active-match state changes. Applied
    // via DOM class + CSS-var mutation so prev/next navigation and topic
    // selection don't trigger a React re-render of TranscriptionTextInner.
    // Deps include `transcriptElements` and `visibleParagraphIds` so we
    // re-apply after a render that adds/removes paragraph DOM nodes.
    useEffect(() => {
      adjustDOMClassesToTopicHighlight(
        scrollRef.current,
        highlightedParagraphIds,
        activeParagraphId,
        paragraphHighlightColors,
      );
    }, [
      highlightedParagraphIds,
      activeParagraphId,
      paragraphHighlightColors,
      visibleParagraphIds,
      transcriptElements,
    ]);

    return (
      <Wrapper>
        <TranscriptionTextInner
          transcriptElements={transcriptElements}
          reelWordStamp={reelWordStamp}
          activeReelSectionId={activeSectionId}
          getPhotoHighlightViewData={getPhotoHighlightViewData}
          onSpeakerClick={onSpeakerClick}
          timelineMarkers={timelineMarkers}
          onMarkerClick={onMarkerClick}
          hidePhotoHighlights={hidePhotoHighlights}
          visibleParagraphIds={visibleParagraphIds}
          visibleElementIndices={visibleElementIndices}
        />
      </Wrapper>
    );
  },
);

const NO_PHOTO_HIGHLIGHT: {
  punchListItem: undefined;
  lastPhotoHighlightActive: false;
  lastPhotoHighlightElementId: undefined;
} = {
  punchListItem: undefined,
  lastPhotoHighlightActive: false,
  lastPhotoHighlightElementId: undefined,
} as const;

type ParagraphGroupWithKey = TranscriptParagraphGroup & {
  groupKey: string;
};

/**
 * Add unique React keys to paragraph groups from the shared utility.
 */
function addGroupKeys(
  groups: TranscriptParagraphGroup[],
): ParagraphGroupWithKey[] {
  const paragraphSubIndex = new Map<string, number>();

  return groups.map((group, i) => {
    const keyBase = group.paragraphId || `group-${i}`;
    const subIdx = paragraphSubIndex.get(keyBase) ?? 0;
    paragraphSubIndex.set(keyBase, subIdx + 1);
    const groupKey = subIdx === 0 ? keyBase : `${keyBase}__${subIdx}`;
    return { ...group, groupKey };
  });
}

const TranscriptionTextInner: React.FC<
  Pick<
    Props,
    | 'transcriptElements'
    | 'onSpeakerClick'
    | 'timelineMarkers'
    | 'onMarkerClick'
    | 'getPhotoHighlightViewData'
    | 'visibleParagraphIds'
    | 'visibleElementIndices'
  > & {
    hidePhotoHighlights?: boolean;
    reelWordStamp?: ReelWordStamp;
    activeReelSectionId?: string;
  }
> = React.memo(
  ({
    transcriptElements,
    getPhotoHighlightViewData,
    onSpeakerClick,
    timelineMarkers,
    onMarkerClick,
    hidePhotoHighlights,
    visibleParagraphIds,
    visibleElementIndices,
    reelWordStamp,
    activeReelSectionId,
  }) => {
    // getSpeakerName/getSpeakerColor always work - they use fallback values
    // until real names are loaded from the cache/API
    const { getSpeakerName, getSpeakerColor } = useSpeakerLabels();
    const paragraphGroups = addGroupKeys(
      groupElementsByParagraph(transcriptElements),
    );

    // Filter out paragraph groups that have no visible content
    // (all elements are removed/cut or whitespace-only)
    const visibleByContent = paragraphGroups.filter(group =>
      group.elements.some(
        ({ element: el }) =>
          el.state !== 'removed' &&
          el.state !== 'cut' &&
          el.value &&
          el.value.trim() !== '',
      ),
    );
    const visibleParagraphGroups = visibleParagraphIds
      ? visibleByContent.filter(
          group =>
            group.paragraphId !== undefined &&
            visibleParagraphIds.has(group.paragraphId),
        )
      : visibleByContent;

    // PLAT-11157 — the clip preview publishes the reel's structure via
    // context (null everywhere else); the per-word stamp arrives as a prop,
    // computed once by the outer component against the same element array.
    const reelView = useReelTranscriptStructure();
    // A Section boundary can fall mid-paragraph; the spanning group is split
    // so every heading sits exactly at its passage's first word. The split
    // runs before the isNewSpeaker recompute — a suffix group keeps its
    // speaker, so no spurious speaker header appears at the cut.
    const groupsForDisplay: ReelSplitGroup<ParagraphGroupWithKey>[] =
      reelWordStamp
        ? splitGroupsAtReelSections(visibleParagraphGroups, reelWordStamp)
        : visibleParagraphGroups;

    // Re-calculate isNewSpeaker for visible groups only
    // (since filtering might have removed groups, we need to recalculate)
    let lastVisibleSpeaker: string | undefined = undefined;
    const visibleGroupsWithSpeakerInfo = groupsForDisplay.map(group => {
      const isNewSpeaker = group.speakerLabel !== lastVisibleSpeaker;
      lastVisibleSpeaker = group.speakerLabel;
      return { ...group, isNewSpeaker };
    });

    const displayRows =
      reelView && reelWordStamp
        ? buildReelTranscriptRows(
            visibleGroupsWithSpeakerInfo,
            reelView.structure,
            reelWordStamp,
            reelView.collapse,
          )
        : visibleGroupsWithSpeakerInfo.map(group => ({
            kind: 'paragraph' as const,
            group,
          }));

    const introMarkers = timelineMarkers?.intro ?? [];
    const endMarkers = timelineMarkers?.end ?? [];
    const nametagsByIndex = timelineMarkers?.nametagsByIndex;

    return (
      <>
        {introMarkers.length > 0 && (
          <MarkerRow
            contentEditable={false}
            suppressContentEditableWarning={true}
          >
            {introMarkers.map(m => (
              <CardChip key={m.id} marker={m} onClick={onMarkerClick} />
            ))}
          </MarkerRow>
        )}
        {displayRows.map((row, rowIndex) => {
          // A heading row directly above already anchors this paragraph's
          // position — a bare same-speaker timestamp right under it stacks a
          // second right-aligned time against the heading's duration and the
          // two read as one confusing blob.
          const followsHeading =
            rowIndex > 0 && displayRows[rowIndex - 1].kind !== 'paragraph';
          if (row.kind === 'chapterHeading') {
            const chapterId = row.chapter.chapterId;
            return (
              <ChapterHeadingRow
                key={`reel-chapter-${chapterId}`}
                chapter={row.chapter}
                speakersByLabel={reelView?.speakersByLabel}
                collapsed={row.collapsed}
                onToggle={
                  reelView?.collapse
                    ? () => reelView.collapse?.toggleChapter(chapterId)
                    : undefined
                }
              />
            );
          }
          if (row.kind === 'gap') {
            return (
              <StructureGapRow
                key={`reel-gap-${row.id}`}
                id={row.id}
                endTime={row.endTime}
              />
            );
          }
          if (row.kind === 'sectionHeading') {
            const sectionId = row.section.sectionId;
            return (
              <SectionHeadingRow
                key={`reel-section-${sectionId}`}
                section={row.section}
                speakersByLabel={reelView?.speakersByLabel}
                collapsed={row.collapsed}
                active={sectionId === activeReelSectionId}
                onSeek={reelView?.onSectionSeek}
                onOpenInEditor={reelView?.onSectionOpenInEditor}
                onRename={reelView?.onSectionRename}
                onGenerateName={reelView?.onSectionGenerateName}
                isGeneratingName={reelView?.generatingNameSectionIds?.has(
                  sectionId,
                )}
                nameError={reelView?.sectionNameErrors?.[sectionId]}
                onToggle={
                  reelView?.collapse
                    ? () => reelView.collapse?.toggleSection(sectionId)
                    : undefined
                }
              />
            );
          }
          const group = row.group;
          return (
            <React.Fragment key={group.groupKey}>
              {/* Speaker header with name + timestamp when speaker changes */}
              {group.isNewSpeaker && group.speakerLabel && (
                <SpeakerHeader
                  contentEditable={false}
                  suppressContentEditableWarning={true}
                >
                  <SpeakerLabel
                    data-testid={`transcript-speaker-label-${group.speakerLabel}`}
                    $color={getSpeakerColor(group.speakerLabel)}
                    $clickable={!!onSpeakerClick}
                    onClick={
                      onSpeakerClick && group.speakerLabel
                        ? e => {
                            e.stopPropagation();
                            onSpeakerClick({
                              speakerLabel: group.speakerLabel as string,
                              // A reel-split slice carries its ORIGINAL
                              // paragraph's indices — a speaker action must
                              // cover the whole paragraph, not the half a
                              // Section boundary left this slice with.
                              sectionIndices:
                                group.reelSpeakerScopeIndices ??
                                group.elements.map(el => el.index),
                              targetElement: e.currentTarget as HTMLElement,
                            });
                          }
                        : undefined
                    }
                  >
                    {getSpeakerName(group.speakerLabel)}
                  </SpeakerLabel>
                  {group.startTime !== undefined && (
                    <SpeakerTime>{formatTime(group.startTime)}</SpeakerTime>
                  )}
                </SpeakerHeader>
              )}
              {/* Timestamp only (minimal spacing) for same speaker or no speaker */}
              {!group.isNewSpeaker &&
                !followsHeading &&
                group.startTime !== undefined && (
                  <TimestampRow
                    contentEditable={false}
                    suppressContentEditableWarning={true}
                  >
                    {formatTime(group.startTime)}
                  </TimestampRow>
                )}
              <ParagraphWrapper
                data-testid={`transcript-paragraph-${group.groupKey}`}
                data-paragraph-id={group.paragraphId}
              >
                {(() => {
                  // Skip leading whitespace elements at the start of the
                  // paragraph — they render as a literal space before the
                  // first word and push the paused-state cursor visually
                  // to the right of the paragraph's true left edge.
                  let seenVisible = false;
                  return group.elements.map(({ element: el, index }) => {
                    if (el.state === 'removed' || el.state === 'cut') {
                      return null;
                    }
                    // Highlights "Show Only": render only the highlighted words.
                    if (
                      visibleElementIndices &&
                      !visibleElementIndices.has(index)
                    ) {
                      return null;
                    }
                    if (
                      !seenVisible &&
                      typeof el.value === 'string' &&
                      el.value.trim() === ''
                    ) {
                      return null;
                    }
                    seenVisible = true;
                    const {
                      punchListItem,
                      lastPhotoHighlightActive,
                      lastPhotoHighlightElementId,
                    } = hidePhotoHighlights
                      ? NO_PHOTO_HIGHLIGHT
                      : getPhotoHighlightViewData(index);
                    const nametags = nametagsByIndex?.get(index);
                    return (
                      <React.Fragment key={index}>
                        <Element
                          value={el.value}
                          state={el.state}
                          index={index}
                          initialIndex={el.initial_index}
                          sourceUuid={el.source_uuid ?? el.id}
                          isPhotoHighlight={!!punchListItem}
                          isLastPhotoHighlight={!!lastPhotoHighlightElementId}
                          isPhotoAssigned={lastPhotoHighlightActive}
                        />
                        {nametags &&
                          nametags.map(m => (
                            <NametagChip
                              key={m.id}
                              marker={m}
                              onClick={onMarkerClick}
                            />
                          ))}
                      </React.Fragment>
                    );
                  });
                })()}
              </ParagraphWrapper>
            </React.Fragment>
          );
        })}
        {endMarkers.length > 0 && (
          <MarkerRow
            contentEditable={false}
            suppressContentEditableWarning={true}
          >
            {endMarkers.map(m => (
              <CardChip key={m.id} marker={m} onClick={onMarkerClick} />
            ))}
          </MarkerRow>
        )}
      </>
    );
  },
  (prevProps, nextProps) =>
    prevProps.transcriptElements === nextProps.transcriptElements &&
    prevProps.timelineMarkers === nextProps.timelineMarkers &&
    prevProps.onMarkerClick === nextProps.onMarkerClick &&
    prevProps.hidePhotoHighlights === nextProps.hidePhotoHighlights &&
    prevProps.visibleParagraphIds === nextProps.visibleParagraphIds &&
    prevProps.visibleElementIndices === nextProps.visibleElementIndices &&
    prevProps.reelWordStamp === nextProps.reelWordStamp &&
    prevProps.activeReelSectionId === nextProps.activeReelSectionId,
);

const Wrapper = styled.div`
  /* Room for the cursor when it starts on the newline. The topic-rail gutter
     lives on the scroll container instead, so the text lines up with the
     toolbar (PLAT-11039). */
  padding-left: ${CURSOR_ALLOWANCE_PX}px;
`;

const ParagraphWrapper = styled.div`
  display: block;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }

  /*
   * Topic-highlight classes are applied imperatively by
   * adjustDOMClassesToTopicHighlight (bypasses React re-renders).
   * The per-paragraph color is set via the --topic-highlight-color
   * inline style on the same element.
   */
  ${topicRailCss}
`;

/**
 * Timestamp row for same-speaker paragraph breaks
 * Right-aligned, pulled up into paragraph gap
 */
const TimestampRow = styled.div`
  text-align: right;
  color: var(--text-muted);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-regular);
  line-height: 1;
  margin-top: -11px;
  margin-bottom: 5px;
  user-select: none;
  pointer-events: none;
`;

/**
 * Block-level row for intro/end card chips that bracket the transcript.
 */
const MarkerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 6px 0 8px;
  user-select: none;
`;

const cursorAnimation = keyframes`
  100% {
    opacity: 0;
  }
`;

// Exported (PLAT-10264) so the Translation Review aligned-blocks view can
// reuse the exact original-language word atom + its state classes instead of
// forking a parallel renderer (epic containment rule D9 — compose the building
// blocks, never edit/duplicate the shared `TranscriptionText` wrapper).
export const WordLight = styled.span`
  color: var(--text-secondary);
  background-color: none;
  position: relative;
  cursor: pointer;
  text-decoration: none;
  opacity: 1;

  &.whitespace {
    white-space: pre-wrap;
  }
  /* PLAT-11765 — "already played" text, the WhatsApp / Spotify transcript
     convention: words the playhead has passed read in the primary text token
     while the rest of the transcript stays in the secondary gray. Declared
     BEFORE every state rule below so the playhead gold, the redline colors and
     the diff greens all still win on equal specificity — read/unread only ever
     paints the words that carry no other meaning. Applied by
     AlignedBlocksView (Translation Review); the prose panes pass no read
     class, so they are unchanged. */
  &.read {
    color: var(--text-primary);
  }
  /* PLAT-11765 — the twin of read: text the playhead has not reached yet. It is
     dimmer than this atom's own default color, which is what the prose panes
     use for ALL text: read against unread at that default was about a 2x
     luminance gap and read as one color across a long transcript, so the unread
     side steps down to muted for about 5x. Only the review emits either class,
     so the prose panes are unchanged. */
  &.unread {
    color: var(--text-muted);
  }
  /* PLAT-11765 — the segment being played reads slightly bolder. Drawn with a
     hairline shadow of the text's own color rather than font-weight ON PURPOSE:
     a real weight change re-measures the text, so the line would re-wrap and
     the rows below would shift every time playback moved on — the instability
     that made a moving caret worse than no marker at all. This adds no metrics,
     so nothing reflows. Applied by AlignedBlocksView (Translation Review); the
     prose panes pass no playing class and are unchanged. */
  &.playing {
    text-shadow: 0 0 0.35px currentColor;
  }
  &.highlighted {
    color: black;
    background-color: #dfb615;
  }
  &.highlighted-by-photo {
    text-decoration: underline;
    color: var(--primary-hover);
  }
  &.state-muted {
    color: #f69b12;
  }
  &.state-removed {
    cursor: default;
    text-decoration: line-through;
    color: indianred;
  }
  &.state-cut {
    cursor: default;
    text-decoration: line-through;
    opacity: 0.6;
    color: #659960;
  }
  &.state-added-with-diff {
    color: #7bb975;
  }

  &.with-cursor {
    &:not(.newline) {
      display: inline-block;
    }
  }
  &.with-cursor::before {
    content: '';
    position: absolute;
    animation: ${cursorAnimation} steps(2) 1.5s infinite;
    /* 26px is tuned for Creator Studio's line height. The compact
       translation-review panes use a smaller font, where 26px is too tall and
       spills above the line — so the review overrides this var to a
       font-relative height on its Surface (PLAT-10286). Default stays 26px so
       Creator Studio / Captions are unchanged. */
    height: var(--with-cursor-caret-height, 26px);
    width: 2px;
    background-color: #dfb615;
    display: inline-block;
    border-radius: 1px;
    left: -2px;
    bottom: 0px;
    will-change: opacity;
  }

  &.with-photo::after {
    display: inline-flex;
    align-items: center;
    vertical-align: baseline;
    margin: 0 4px;
    padding: 3px 6px 3px 5px;
    border-radius: 3px;
    font-size: 10px;
    line-height: 1;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-weight: var(--font-weight-medium);
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.14);
    user-select: none;
    white-space: nowrap;
    color: var(--text-secondary);
  }
  &.with-photo--assigned::after {
    content: url('/svgs/TimelinePhotoIcon_active.svg') ' Photo';
  }
  &.with-photo--unassigned::after {
    content: url('/svgs/TimelinePhotoIcon_inactive.svg') ' Photo';
  }

  ${searchHighlightStyles}
  ${quoteHighlightStyles}
  ${clipHighlightStyles}
  ${savedSegmentHighlightStyles}
  ${overlayHighlightStyles}

  &:hover {
    color: lightgreen !important;
  }
`;

const colorByState = (
  state: string,
  isHighlighted: boolean,
  isPhotoHighlight: boolean,
  diff?: boolean,
) => {
  if (isHighlighted) return 'black';
  if (state === 'muted') return '#F69B12';
  if (isPhotoHighlight) return 'var(--primary-hover)';
  return (
    {
      ...(diff && { added: '#7bb975' }),
      removed: 'indianred',
      cut: '#659960', // '#7bb975',
      muted: '#F69B12',
    }[state] || 'var(--text-secondary)'
  );
};

export const Word = styled.span.attrs(
  (props: {
    isHighlighted?: boolean;
    isCut?: boolean;
    isText?: boolean;
    state?: 'added' | 'removed' | 'muted' | 'replaced' | 'cut';
    isPhotoHighlight?: boolean;
    diff?: boolean;
    isWhitespace?: boolean;
    // cursor: 'after' | 'before' | 'none';
  }) => props,
)`
  color: ${props =>
    colorByState(
      props.state || '',
      props.isHighlighted || false,
      props.isPhotoHighlight || false,
      props.diff || false,
    )};
  background-color: ${props => (props.isHighlighted ? '#dfb615' : 'none')};
  cursor: ${props =>
    props.state !== 'removed' && props.state !== 'cut' ? 'pointer' : 'default'};
  text-decoration: ${props =>
    props.state === 'removed' || props.state === 'cut'
      ? 'line-through'
      : props.isPhotoHighlight
        ? 'underline'
        : 'none'};
  position: relative;
  opacity: ${props => (props.isCut ? 0.6 : 1)};

  ${props => props.isWhitespace && `white-space: pre-wrap;`}

  &:hover {
    color: lightgreen;
  }
`;

// ${(props) => props.cursor === 'after' && css`
//     &::after {
//       content: '';
//       position: absolute;
//       animation: ${cursorAnimation} steps(2) 1.5s infinite;
//       height: 26px;
//       width: 3px;
//       background-color: #2a85ff;
//       display: inline-block;
//       border-radius: 1px;
//       right: -2px;
//       bottom: -4px;
//     }
//   `}

export const Cursor = styled.span
  .attrs({
    'data-testid': 'transcript-cursor',
  })
  .withConfig({
    displayName: 'TranscriptCursor',
  })`
  animation: ${cursorAnimation} steps(2) 1.5s infinite;
  height: 26px;
  margin-bottom: -8px;
  width: 2px;
  background-color: #dfb615;
  display: inline-block;
  border-radius: 1px;
`;

export const HighlightImageWrapper = styled.div.withConfig({
  displayName: 'TranscriptHighlightWrapper',
})`
  position: relative;
  display: inline;
`;

export const HighlightImage = styled.div
  .withConfig({
    displayName: 'TranscriptHighlight',
  })
  .attrs((props: { isAssigned: boolean }) => props)`
  background: ${props =>
    props.isAssigned
      ? 'linear-gradient(0deg, var(--primary-hover) 0%, var(--primary-hover) 100%), var(--primary-hover)'
      : 'linear-gradient(0deg, #4f4f4f 0%, #4f4f4f 100%), lightgray'}
    50% / cover no-repeat;
  color: #4f4f4f;
  border-radius: 50%;
  padding: 6px;
  margin-left: 5px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
`;

// Utilities for a CSS class-based approach of bypassing react re-renders

function elementPropsToWordClassName(props: Partial<ElementProps>): string {
  const {
    value,
    state,
    isPhotoHighlight,
    isLastPhotoHighlight,
    isPhotoAssigned,
  } = props;
  return [
    state && `state-${state}`,
    isPhotoHighlight && 'highlighted-by-photo',
    isLastPhotoHighlight && 'with-photo',
    isLastPhotoHighlight &&
      (isPhotoAssigned ? 'with-photo--assigned' : 'with-photo--unassigned'),
    typeof value === 'string' && value.trim() === '' && 'whitespace',
    value === '\n' && 'newline',
  ]
    .filter(Boolean)
    .join(' ');
}

// Exported (PLAT-10264) so the Translation Review aligned-blocks view derives
// identical state classes (`state-removed` / `state-cut` / `whitespace` / …)
// on its reused `WordLight` atoms. See the export note on `WordLight` above.
export function transcriptElementToWordClassName(
  el: TranscriptElement,
  index: number,
  getPhotoHighlightViewData?: Props['getPhotoHighlightViewData'],
  hidePhotoHighlights?: boolean,
): string {
  // `getPhotoHighlightViewData` is optional (PLAT-10264): the Translation
  // Review aligned view has no punch-list/photo context, so it omits the
  // resolver and falls back to the no-photo sentinel — same branch the
  // existing `hidePhotoHighlights` callers take.
  const {
    punchListItem,
    lastPhotoHighlightActive,
    lastPhotoHighlightElementId,
  } =
    hidePhotoHighlights || !getPhotoHighlightViewData
      ? NO_PHOTO_HIGHLIGHT
      : getPhotoHighlightViewData(index);

  const props: Partial<ElementProps> = {
    value: el.value,
    state: el.state,
    index: index,
    isPhotoHighlight: !!punchListItem,
    isLastPhotoHighlight: !!lastPhotoHighlightElementId,
    isPhotoAssigned: lastPhotoHighlightActive,
  };

  return elementPropsToWordClassName(props);
}

// Classes added imperatively by highlight features (quotes, clips, search)
// that should not be removed by the element-state diff.
const PRESERVED_CLASS_PREFIXES = ['quote-match', 'clip-match', 'search-match'];

function calculateClassesDiff(
  newClasses: string[],
  prevClasses: string[],
): { added: string[]; removed: string[] }[] {
  return newClasses.map((newClass, index) => {
    if (newClass.includes('state-removed') || newClass.includes('state-cut')) {
      return { added: [], removed: [] };
    }
    const prevClass = prevClasses[index] || '';
    const added = newClass.split(' ').filter(c => !prevClass.includes(c));
    const removed = prevClass
      .split(' ')
      .filter(
        c =>
          !newClass.includes(c) &&
          !PRESERVED_CLASS_PREFIXES.some(prefix => c.startsWith(prefix)),
      );
    return { added, removed };
  });
}

function adjustDOMClassesToElementStateUpdates(
  newClasses: string[],
  prevClasses: string[],
  scrollRef: React.RefObject<HTMLDivElement>,
) {
  return new Promise<void>(resolve => {
    const prevClassesChunked = chunk(prevClasses, 2500);
    const newClassesChunked = chunk(newClasses, 2500);

    for (let i = 0; i < newClassesChunked.length; i++) {
      setTimeout(() => {
        const classesDiff = calculateClassesDiff(
          newClassesChunked[i],
          prevClassesChunked[i] ?? [],
        );
        const updates = [];
        for (let j = 0; j < classesDiff.length; j++) {
          const { added, removed } = classesDiff[j];
          if (added.length === 0 && removed.length === 0) {
            continue;
          }
          updates.push({
            added,
            removed,
            domElement: scrollRef.current?.querySelector(
              `[data-index='${j + i * newClassesChunked[i].length}']`,
            ),
          });
        }

        for (const { added, removed, domElement } of updates) {
          if (!domElement) {
            continue;
          }
          if (added.length > 0) {
            domElement.classList.add(...added);
          }
          if (removed.length > 0) {
            domElement.classList.remove(...removed);
          }
        }

        if (i === newClassesChunked.length - 1) {
          resolve();
        }
      }, i * 250);
    }
  });
}

function adjustDOMClassesToCursor(
  newCursor: {
    from: number;
    to: number;
  },
  prevCursor: { from: number; to: number } | null,
  scrollRef: React.RefObject<HTMLDivElement>,
  isPlaying: boolean,
) {
  if (!scrollRef.current) {
    return;
  }

  // split selecting and modifying into 2 steps to prevent layout thrashing

  const oldDOMList = [];
  const newDOMList = [];

  if (prevCursor) {
    for (let i = prevCursor.from; i < prevCursor.to; i++) {
      oldDOMList.push(scrollRef.current.querySelector(`[data-index='${i}']`));
    }
  }
  for (let i = newCursor.from; i < newCursor.to; i++) {
    newDOMList.push(scrollRef.current.querySelector(`[data-index='${i}']`));
  }

  for (const el of oldDOMList) {
    if (!el) {
      continue;
    }
    el.classList.remove('highlighted', 'with-cursor');
  }
  for (let i = newCursor.from, j = 0; i < newCursor.to; i++, j++) {
    const el = newDOMList[j];
    if (!el) {
      continue;
    }
    if (isPlaying) {
      el.classList.add('highlighted');
    } else if (i === newCursor.from) {
      el.classList.add('with-cursor');
    }
  }
}
